import { useEffect, useRef } from 'react';
import { Message as MessageType } from '@/types/chat.types';
import { Message } from './Message';

interface ChatHistoryProps {
  messages: MessageType[];
  isLoading: boolean;
  onEmailRequest?: (message: string) => void;
  currentWelcomeMessage?: string;
  prevWelcomeMessage?: string;
  isWelcomeTransitioning?: boolean;
}

export const ChatHistory = ({ messages, isLoading, onEmailRequest, currentWelcomeMessage, prevWelcomeMessage, isWelcomeTransitioning }: ChatHistoryProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className="flex-1 overflow-y-auto px-6 py-8 bg-neutral-white dark:bg-neutral-black">
      <div className="max-w-4xl mx-auto">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <h2 className="text-h1 font-semibold text-neutral-black dark:text-neutral-white mb-6 tracking-tight">
              Welcome to ChatHTL
            </h2>
            <div className="relative w-full md:max-w-lg mx-auto px-4 md:px-0" style={{ minHeight: '120px' }}>
              {/* Previous message fading out */}
              <p
                className="absolute inset-0 text-base md:text-lg text-neutral-medium-gray dark:text-neutral-medium-gray leading-relaxed transition-opacity duration-1000"
                style={{ opacity: isWelcomeTransitioning ? 0 : 1 }}
              >
                {prevWelcomeMessage}
              </p>
              {/* Current message fading in */}
              <p
                className="absolute inset-0 text-base md:text-lg text-neutral-medium-gray dark:text-neutral-medium-gray leading-relaxed transition-opacity duration-1000"
                style={{ opacity: isWelcomeTransitioning ? 1 : 0 }}
              >
                {currentWelcomeMessage}
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <Message key={message.id} message={message} onEmailRequest={onEmailRequest} />
            ))}
            {isLoading && (
              <div className="flex justify-start mb-6">
                <div className="bg-neutral-light-gray dark:bg-neutral-dark-gray rounded-2xl px-5 py-4 shadow-sm">
                  <img
                    src="/images/logos/loader-dots.svg"
                    alt="Ella is thinking..."
                    className="h-10"
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
